// utils/routeHelper.js
import { CATEGORIES, LOCALIZED_PATHS, PATHS } from "../constants/pathnames.js";
import { routing } from "../i18n/routing.js";

// Single source of truth: routing.locales. Hardcoding the locale list duplicated
// the i18n config and would silently drift if a new locale (e.g. fr) was added.
const SUPPORTED_LOCALES = routing.locales;
const routeEntries = Object.entries(PATHS);
const pathPatternCache = new Map();

// Result cache for getRouteDetails — same pathname returns same object reference,
// allowing consumers to use it as a useMemo dependency. LRU-light eviction.
const ROUTE_DETAILS_CACHE_LIMIT = 50;
const routeDetailsCache = new Map();

// ============================================
// CORE FUNCTIONS
// ============================================

/**
 * Gelen pathname'den query ve hash her zaman temizlenir; route kararları sadece path üzerinden verilir.
 * Seçenek yok: her çağrıda önce temizle, sonra PATHS'e göre sorgula.
 */
function normalizePathname(pathname) {
  if (!pathname || typeof pathname !== "string") return pathname || "";

  const queryIndex = pathname.indexOf("?");
  const hashIndex = pathname.indexOf("#");
  const cutIndex = [queryIndex, hashIndex]
    .filter((index) => index !== -1)
    .sort((a, b) => a - b)[0];

  let pathOnly =
    cutIndex === undefined ? pathname : pathname.slice(0, cutIndex);

  try {
    pathOnly = decodeURI(pathOnly);
  } catch {
    // Keep the original value if the URL contains a malformed escape sequence.
  }

  if (!pathOnly.startsWith("/")) pathOnly = `/${pathOnly}`;
  if (pathOnly.length > 1) pathOnly = pathOnly.replace(/\/+$/, "");

  return pathOnly || "/";
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripLocalePrefix(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];

  if (!SUPPORTED_LOCALES.includes(firstSegment)) return pathname;

  const unprefixed = segments.slice(1).join("/");
  return unprefixed ? `/${unprefixed}` : "/";
}

// Strip every consecutive leading locale segment so malformed mixed-prefix
// URLs like "/en/de/flugticket" can still be matched against the canonical
// route table. Without this, only the first locale would be peeled, leaving
// a still-prefixed candidate ("/de/flugticket") that the index lookup misses
// when the resolution path was already stale.
function stripAllLeadingLocales(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  let i = 0;
  while (i < segments.length && SUPPORTED_LOCALES.includes(segments[i])) {
    i += 1;
  }
  if (i === 0) return pathname;
  const remaining = segments.slice(i).join("/");
  return remaining ? `/${remaining}` : "/";
}

function getPathCandidates(pathname) {
  const normalized = normalizePathname(pathname);
  const oneStrip = stripLocalePrefix(normalized);
  const allStrip = stripAllLeadingLocales(normalized);

  return [...new Set([normalized, oneStrip, allStrip])];
}

function getLocalizedPathTemplates(internalPath) {
  const localizedPath = LOCALIZED_PATHS[internalPath];

  if (!localizedPath) return [];
  if (typeof localizedPath === "string") return [localizedPath];

  return Object.values(localizedPath);
}

function hasDynamicSegment(pathTemplate) {
  return pathTemplate.includes("[");
}

function createPathPattern(pathTemplate) {
  const normalizedTemplate = normalizePathname(pathTemplate);

  if (pathPatternCache.has(normalizedTemplate)) {
    return pathPatternCache.get(normalizedTemplate);
  }

  if (!hasDynamicSegment(normalizedTemplate)) {
    pathPatternCache.set(normalizedTemplate, null);
    return null;
  }

  const segmentPatterns = normalizedTemplate
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      if (/^\[\[\.\.\.[^\]]+\]\]$/.test(segment)) return ".*";
      if (/^\[\.\.\.[^\]]+\]$/.test(segment)) return ".+";
      if (/^\[[^\]]+\]$/.test(segment)) return "[^/]+";
      return escapeRegExp(segment);
    });

  const pattern = new RegExp(`^/${segmentPatterns.join("/")}$`);
  pathPatternCache.set(normalizedTemplate, pattern);

  return pattern;
}

function matchesPathTemplate(pathTemplate, pathname) {
  const normalizedTemplate = normalizePathname(pathTemplate);
  const normalizedPathname = normalizePathname(pathname);

  if (normalizedTemplate === normalizedPathname) return true;

  const pattern = createPathPattern(normalizedTemplate);
  return pattern ? pattern.test(normalizedPathname) : false;
}

function getRouteSpecificity(pathTemplate) {
  const normalizedTemplate = normalizePathname(pathTemplate);
  const segments = normalizedTemplate.split("/").filter(Boolean);
  const dynamicSegments = segments.filter((segment) => segment.includes("["));
  const catchAllSegments = dynamicSegments.filter((segment) =>
    segment.includes("..."),
  );
  const staticLength = segments
    .filter((segment) => !segment.includes("["))
    .join("/").length;

  return {
    segmentCount: segments.length,
    catchAllCount: catchAllSegments.length,
    dynamicCount: dynamicSegments.length,
    staticLength,
  };
}

const sortedRouteEntries = [...routeEntries].sort(([, left], [, right]) => {
  const a = getRouteSpecificity(left.PATH);
  const b = getRouteSpecificity(right.PATH);

  return (
    b.segmentCount - a.segmentCount ||
    a.catchAllCount - b.catchAllCount ||
    a.dynamicCount - b.dynamicCount ||
    b.staticLength - a.staticLength
  );
});

// Query-aware paths (e.g. SMART_FLIGHT_TICKET = "/all-services?advantage=...")
// must NOT be indexed by base path — otherwise they would collide with the
// query-less route at the same path (ADVANTAGES = "/all-services") and
// shadow it on a path-only lookup.
function hasQueryString(template) {
  return template.includes("?");
}

// Reverse index: localized non-dynamic template → owning routeEntries entry.
// First registration wins so PATHS insertion order remains the tiebreaker for
// any (theoretical) localized-path collisions.
const localizedExactIndex = (() => {
  const index = new Map();
  for (const entry of routeEntries) {
    const [, value] = entry;
    if (hasQueryString(value.PATH)) continue;
    const cfg = LOCALIZED_PATHS[value.PATH];
    if (!cfg) continue;
    const templates = typeof cfg === "string" ? [cfg] : Object.values(cfg);
    for (const tpl of templates) {
      if (hasDynamicSegment(tpl)) continue;
      if (hasQueryString(tpl)) continue;
      const norm = normalizePathname(tpl);
      if (!index.has(norm)) index.set(norm, entry);
    }
  }
  return index;
})();

function buildRouteInfo(key, value) {
  return {
    key,
    path: value.PATH,
    categories: value.CATEGORIES,
  };
}

/**
 * Pathname ile route bilgilerini bul.
 * Her seferinde gelen pathname'den query temizlenir, sonra PATHS sorgulanır.
 */
export function getRouteInfo(pathname) {
  if (!pathname) {
    return {
      key: null,
      path: null,
      categories: [],
    };
  }

  const pathCandidates = getPathCandidates(pathname);

  for (const candidate of pathCandidates) {
    for (const [key, value] of routeEntries) {
      // Skip query-aware paths (e.g. SMART_FLIGHT_TICKET) on path-only match —
      // they would otherwise shadow the query-less route at the same base
      // path (e.g. ADVANTAGES). A query-aware match would need an explicit
      // query-comparison branch (out of scope here).
      if (hasQueryString(value.PATH)) continue;
      if (normalizePathname(value.PATH) === candidate) {
        return buildRouteInfo(key, value);
      }
    }
  }

  for (const candidate of pathCandidates) {
    const indexed = localizedExactIndex.get(candidate);
    if (indexed) {
      const [key, value] = indexed;
      return buildRouteInfo(key, value);
    }
  }

  for (const candidate of pathCandidates) {
    for (const [key, value] of sortedRouteEntries) {
      if (hasQueryString(value.PATH)) continue;
      if (matchesPathTemplate(value.PATH, candidate)) {
        return buildRouteInfo(key, value);
      }

      const localizedTemplates = getLocalizedPathTemplates(value.PATH).filter(
        (template) => !hasQueryString(template),
      );

      if (
        localizedTemplates.some((template) =>
          matchesPathTemplate(template, candidate),
        )
      ) {
        return buildRouteInfo(key, value);
      }
    }
  }

  // Bulunamadıysa boş dön. path'i null bırakıyoruz — pathCandidates[0]
  // hala locale-prefixed olabilir ve tüketici (örn. router.replace) bunu
  // raw href olarak kabul edip yeni bir locale prefix daha ekleyerek
  // /en/de/... gibi çift prefix bug'ı üretir.
  return {
    key: null,
    path: null,
    categories: [],
  };
}

// Re-export so consumers (örn. useLanguageAndCurrency) defansif olarak
// kullanıcı-yazımlı veya stale pathname'lerden locale prefix'i temizleyebilsin.
export { stripAllLeadingLocales };

/**
 * Category kontrolü - bir route'un belirli kategoriye sahip olup olmadığını kontrol eder
 */
export function hasCategory(routeInfo, category) {
  return routeInfo?.categories?.includes(category);
}

/**
 * Multiple category kontrolü - birden fazla kategorinin hepsini içeriyor mu?
 */
export function hasAllCategories(routeInfo, categories) {
  if (!routeInfo?.categories) return false;
  return categories.every((cat) => routeInfo.categories.includes(cat));
}

/**
 * Any category kontrolü - verilen kategorilerden en az birini içeriyor mu?
 */
export function hasAnyCategory(routeInfo, categories) {
  if (!routeInfo?.categories) return false;
  return categories.some((cat) => routeInfo.categories.includes(cat));
}

// ============================================
// MAIN API
// ============================================

/**
 * Route detaylarını döndürür
 * Object destructuring ile kullanılabilir
 *
 * @example
 * const pathname = usePathname();
 * const routeDetails = getRouteDetails(pathname);
 * const { isFlightHome, isHotelDetail } = routeDetails;
 */
export function getRouteDetails(pathname) {
  const cacheKey = pathname == null ? "" : String(pathname);
  if (routeDetailsCache.has(cacheKey)) {
    return routeDetailsCache.get(cacheKey);
  }

  const pathOnly = normalizePathname(pathname);
  const routeInfo = getRouteInfo(pathOnly);
  const routePath = routeInfo.path;

  const details = {
    pathname: pathOnly,
    key: routeInfo.key,
    path: routeInfo.path,
    categories: routeInfo.categories,

    // Home Pages
    isHome: hasCategory(routeInfo, CATEGORIES.HOME),
    isFlightHome: hasCategory(routeInfo, CATEGORIES.FLIGHT_HOME),
    isBusTicketHome: hasCategory(routeInfo, CATEGORIES.BUS_HOME),
    isHotelHome: hasCategory(routeInfo, CATEGORIES.HOTEL_HOME),
    isFerryTicketHome: hasCategory(routeInfo, CATEGORIES.FERRY_HOME),
    isCarRentalHome: hasCategory(routeInfo, CATEGORIES.CAR_HOME),

    // Landing Pages
    isLanding: hasCategory(routeInfo, CATEGORIES.LANDING),
    isFlightTicketLanding: hasCategory(routeInfo, CATEGORIES.FLIGHT_LANDING),
    isBusTicketLanding: hasCategory(routeInfo, CATEGORIES.BUS_LANDING),
    isHotelLanding: hasCategory(routeInfo, CATEGORIES.HOTEL_LANDING),
    isFerryTicketLanding: hasCategory(routeInfo, CATEGORIES.FERRY_LANDING),
    isCarRentalLanding: hasCategory(routeInfo, CATEGORIES.CAR_LANDING),

    // Result Pages
    isFlightResultPage: hasCategory(routeInfo, CATEGORIES.FLIGHT_RESULT),
    isBusResultPage: hasCategory(routeInfo, CATEGORIES.BUS_RESULT),
    isHotelResultPage: hasCategory(routeInfo, CATEGORIES.HOTEL_RESULT),
    isFerryResultPage: hasCategory(routeInfo, CATEGORIES.FERRY_RESULT),
    isCarResultPage: hasCategory(routeInfo, CATEGORIES.CAR_RESULT),

    // Flight Specific
    isFlightTicket: hasCategory(routeInfo, CATEGORIES.FLIGHT),
    isFlightTicketOneWay: hasCategory(routeInfo, CATEGORIES.FLIGHT_ONE_WAY),
    isFlightTicketRoundTrip: hasCategory(
      routeInfo,
      CATEGORIES.FLIGHT_ROUND_TRIP,
    ),
    isFlightTicketMultiCity: hasCategory(
      routeInfo,
      CATEGORIES.FLIGHT_MULTI_CITY,
    ),
    isFlightTicketCampaigns: hasCategory(
      routeInfo,
      CATEGORIES.FLIGHT_CAMPAIGNS,
    ),
    isFlightReservation: hasCategory(routeInfo, CATEGORIES.FLIGHT_RESERVATION),

    // Bus Specific
    isBusTicket: hasCategory(routeInfo, CATEGORIES.BUS),
    isBusTicketOneWay: hasCategory(routeInfo, CATEGORIES.BUS_ONE_WAY),
    isBusReservation: hasCategory(routeInfo, CATEGORIES.BUS_RESERVATION),

    // Hotel Specific
    isHotel: hasCategory(routeInfo, CATEGORIES.HOTEL),
    isHotelDetail: hasCategory(routeInfo, CATEGORIES.HOTEL_DETAIL),
    isHotelReservation: hasCategory(routeInfo, CATEGORIES.HOTEL_RESERVATION),

    // Ferry Specific
    isFerryTicket: hasCategory(routeInfo, CATEGORIES.FERRY),
    isFerryTicketOneWay: hasCategory(routeInfo, CATEGORIES.FERRY_ONE_WAY),
    isFerryTicketRoundTrip: hasCategory(routeInfo, CATEGORIES.FERRY_ROUND_TRIP),
    isFerryReservation: hasCategory(routeInfo, CATEGORIES.FERRY_RESERVATION),

    // Car Specific
    isCarRental: hasCategory(routeInfo, CATEGORIES.CAR),
    isCarReservationPage: hasCategory(routeInfo, CATEGORIES.CAR_RESERVATION),

    // General Pages
    isReservation: hasCategory(routeInfo, CATEGORIES.RESERVATION),
    isPayment: hasCategory(routeInfo, CATEGORIES.PAYMENT),
    isAccountPage: hasCategory(routeInfo, CATEGORIES.ACCOUNT),
    isCommon: hasCategory(routeInfo, CATEGORIES.CORE),

    // Static Pages
    isAboutUsPage: routePath === PATHS.ABOUT_US.PATH,
    isContactPage: routePath === PATHS.CONTACT.PATH,
    isAdvantagesPage:
      routePath === PATHS.ADVANTAGES.PATH ||
      routePath === PATHS.ONLINE_CHANGE_SERVICE.PATH ||
      routePath === PATHS.UNCONDITIONAL_CANCELLATION_SERVICE.PATH ||
      routePath === PATHS.FREE_LIVE_SUPPORT.PATH,
    isPrivacyAndSecurityPage:
      hasAnyCategory(routeInfo, [CATEGORIES.CORE]) &&
      (routePath === PATHS.PRIVACY_POLICY.PATH ||
        routePath === PATHS.COOKIE_POLICY.PATH ||
        routePath === PATHS.TERMS_OF_USE.PATH ||
        routePath === PATHS.DATA_PROTECTION.PATH ||
        routePath === PATHS.COMMERCIAL_MESSAGE_CONSENT.PATH),

    // UI States
    shouldHideFooter: hasCategory(routeInfo, CATEGORIES.HIDE_FOOTER),
    shouldBlurHeader: hasCategory(routeInfo, CATEGORIES.BLURRY_HEADER),
    shouldHideNavbar: hasCategory(routeInfo, CATEGORIES.HIDDEN_NAVBAR),

    // Combined Helpers
    isAnyProductHome: hasAnyCategory(routeInfo, [
      CATEGORIES.FLIGHT_HOME,
      CATEGORIES.BUS_HOME,
      CATEGORIES.HOTEL_HOME,
      CATEGORIES.FERRY_HOME,
      CATEGORIES.CAR_HOME,
    ]),
    isAnyResultPage: hasAnyCategory(routeInfo, [
      CATEGORIES.FLIGHT_RESULT,
      CATEGORIES.BUS_RESULT,
      CATEGORIES.HOTEL_RESULT,
      CATEGORIES.FERRY_RESULT,
      CATEGORIES.CAR_RESULT,
    ]),
    isAnyReservationPage: hasAnyCategory(routeInfo, [
      CATEGORIES.FLIGHT_RESERVATION,
      CATEGORIES.BUS_RESERVATION,
      CATEGORIES.HOTEL_RESERVATION,
      CATEGORIES.FERRY_RESERVATION,
      CATEGORIES.CAR_RESERVATION,
    ]),
  };

  if (routeDetailsCache.size >= ROUTE_DETAILS_CACHE_LIMIT) {
    const oldestKey = routeDetailsCache.keys().next().value;
    routeDetailsCache.delete(oldestKey);
  }
  routeDetailsCache.set(cacheKey, details);

  return details;
}

// ============================================
// Debug Helper
// ============================================

/**
 * Development için - mevcut route bilgilerini logla
 */
export function debugRouteInfo(pathname) {
  return getRouteInfo(pathname);
}
